import { FormHTMLAttributes, useState } from 'react'
import { useFormik } from 'formik'
import parse from 'html-react-parser'

import FormControl from '@/components/base/form-control'
import Spinner from '@/components/base/spinner'
import SVGIcon from '@/components/base/svg-icon'
import {
  convertJsontoFormData,
  createInitialValues,
  createYupSchema
} from '@/utils/form'
import { API } from '@/constants'
import { axiosServer as axios } from '@/base/axios'

import { cn } from '@/lib/utils'
import { WPCF7Form } from '@/types'

export type FormProps = FormHTMLAttributes<HTMLFormElement> &
  Partial<{
    form_id: string
    submit: string
    fields: WPCF7Form['form']
    theme?: 'dark' | 'light'
  }>

const Form = ({
  form_id,
  fields = [],
  submit,
  className,
  theme = 'light',
  ...props
}: FormProps) => {
  const [statusMessage, setStatusMessage] = useState('&nbsp;')

  async function sendContactForm(sendData: any) {
    const headers = {
      'Content-Type': 'multipart/form-data'
    }
    const response = await axios.post(
      `${API.GRAVITY_FORM}/${form_id}/submissions`,
      convertJsontoFormData(sendData),
      { headers }
    )
    return response
  }

  const formik = useFormik<any>({
    initialValues: createInitialValues(fields) || {},
    validationSchema: createYupSchema(fields, 'it'),
    enableReinitialize: true,
    onSubmit: (values, { setStatus, setSubmitting, resetForm }) => {
      const form_url_id = fields.find((field) => field.label === 'form_url')?.id
      const result = sendContactForm({
        ...values,
        ...(form_url_id
          ? { [`input_${form_url_id}`]: window.location.href }
          : {})
      })
      result
        .then((response) => {
          setStatusMessage(response.data.confirmation_message)
          setStatus(response.data.is_valid ? 'success' : 'error')
        })
        .then(() => {
          setTimeout(() => {
            resetForm()
            setStatusMessage('&nbsp;')
            setStatus('')

            setSubmitting(false)
          }, 3000)
        })
    }
  })

  return (
    <form
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
      className={cn('grid gap-y-[1.5rem]', className)}
      {...props}
    >
      {fields?.map((field, index) => (
        <FormControl
          key={index}
          theme={theme}
          id={`input_${field.id}`}
          name={`input_${field.id}`}
          type={field.type}
          label={field.type === 'consent' ? field.checkboxLabel : field.label}
          required={field.isRequired}
          options={field.choices}
          placeholder={`${field.placeholder}${field.isRequired ? ' *' : ''}`}
          checked={formik.values[`input_${field.id}`] || false}
          value={formik.values[`input_${field.id}`]}
          disabled={formik.isSubmitting}
          onChange={(e) => {
            formik.handleChange(e)
            if (field.type === 'consent') {
              formik.setFieldValue(
                `input_${field.id}_1`,
                e.target.checked ? '1' : '0',
                false
              )
            }
          }}
          onBlur={formik.handleBlur}
          error={
            formik.errors[`input_${field.id}`] !== undefined &&
            formik.touched[`input_${field.id}`] !== undefined
          }
        >
          {field.type === 'email' ? (
            <button
              type="submit"
              disabled={formik.isSubmitting}
              className={cn(
                'absolute right-0 top-0 h-[4rem] w-[4rem]',
                'flex items-center justify-center rounded-full'
              )}
            >
              <span className="sr-only">{submit}</span>
              {formik.isSubmitting ? (
                <Spinner variant="small" className="[&>svg]:stroke-copper" />
              ) : (
                <SVGIcon
                  symbol="submit-icon"
                  className="h-[1.2rem] w-[1.8rem] fill-copper"
                />
              )}
            </button>
          ) : null}
        </FormControl>
      ))}

      {statusMessage !== '' ? (
        <div
          className={cn('flex font-gillsans text-small font-light', {
            'text-copper': formik.status !== 'error',
            'text-error': formik.status === 'error'
          })}
        >
          {parse(statusMessage)}
        </div>
      ) : null}
    </form>
  )
}

export default Form
